import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';

interface Department {
  ref: string;
  title: string;
  parentName: string;
}

export const DepartmentPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const catalogApi = useApi(catalogApiRef);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);

  // Read the currently selected division from the sibling form field
  const parentDivision: string = formContext?.formData?.division || '';

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        setLoading(true);
        setError(null);

        const { items: departmentsResult } = await catalogApi.getEntities({
          filter: { kind: 'Group', 'spec.type': 'department' },
          fields: [
            'metadata.name',
            'metadata.title',
            'metadata.namespace',
            'spec.parent',
          ],
        });

        const deptList: Department[] = departmentsResult
          .map((d: any) => ({
            ref: `group:${d.metadata.namespace || 'default'}/${
              d.metadata.name
            }`,
            title: d.metadata.title || d.metadata.name,
            parentName: d.spec?.parent || '',
          }))
          .sort((a: Department, b: Department) =>
            a.title.localeCompare(b.title),
          );

        setAllDepartments(deptList);
        setLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load from catalog',
        );
        setLoading(false);
      }
    };

    fetchEntities();
  }, [catalogApi]);

  // Clear department selection when division changes and the current value no longer belongs to it
  useEffect(() => {
    if (formData && parentDivision) {
      const stillValid = allDepartments.some(
        d => d.ref === formData && d.parentName === parentDivision,
      );
      if (!stillValid) {
        onChange('');
      }
    }
  }, [parentDivision, allDepartments, formData, onChange]);

  const filteredDepartments = parentDivision
    ? allDepartments.filter(d => d.parentName === parentDivision)
    : allDepartments;

  const handleDepartmentChange = (event: any) => {
    onChange(event.target.value || '');
  };

  if (loading) {
    return (
      <FormControl fullWidth margin="normal" disabled>
        <InputLabel id="department-select-label">Department</InputLabel>
        <Select labelId="department-select-label" disabled>
          <MenuItem disabled>
            <CircularProgress size={16} style={{ marginRight: 8 }} /> Loading...
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth margin="normal" error>
        <InputLabel id="department-select-label">Department</InputLabel>
        <Select labelId="department-select-label" disabled>
          <MenuItem disabled>Error loading data</MenuItem>
        </Select>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={rawErrors && rawErrors.length > 0}
    >
      <InputLabel id="department-select-label">Department</InputLabel>
      <Select
        labelId="department-select-label"
        id="department-select"
        value={formData || ''}
        onChange={handleDepartmentChange}
      >
        <MenuItem value="">
          <em>Select a department...</em>
        </MenuItem>
        {filteredDepartments.map(dept => (
          <MenuItem key={dept.ref} value={dept.ref}>
            {dept.title}
          </MenuItem>
        ))}
      </Select>
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText>
          {rawErrors.map((err: any) => err.stack).join(', ')}
        </FormHelperText>
      )}
    </FormControl>
  );
};
